import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field } from "@/components/ui/Field";
import { Textarea } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Spinner } from "@/components/ui/Spinner";
import { Table, Td, Th } from "@/components/ui/Table";
import { useToast } from "@/hooks/useToast";
import { qaFlags as qaApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { RULE_EXPLANATIONS } from "@/lib/qa";
import { strings } from "@/lib/strings";
import type { QAFlag } from "@/lib/types";

/** The QA flags queue (docs/08 §8.7, docs/03 §3.5). */
export function FlagQueue() {
  const { notify } = useToast();
  const [flags, setFlags] = useState<QAFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [severity, setSeverity] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [resolving, setResolving] = useState(false);
  const [reason, setReason] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setFlags(await qaApi.list({ severity: severity || undefined, resolved: false }));
    } catch {
      notify(strings.errors.network, "error");
    } finally {
      setLoading(false);
    }
  }, [severity, notify]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const resolve = async () => {
    // F-04: a reason is required and goes into the audit log.
    if (reason.trim().length < 3) return;
    setResolving(true);
    try {
      await qaApi.bulkResolve([...selected], reason.trim());
      notify(`Resolved ${selected.size} flag${selected.size === 1 ? "" : "s"}.`, "success");
      setSelected(new Set());
      setReason("");
      setDialogOpen(false);
      await load();
    } catch {
      notify(strings.errors.generic, "error");
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold text-slate-900">{strings.qa.queueTitle}</h1>
        <div className="flex items-center gap-2">
          <Select
            aria-label="Severity"
            placeholder="All severities"
            value={severity}
            onChange={(event) => setSeverity(event.target.value)}
            options={[
              { value: "blocking", label: "Blocking" },
              { value: "warning", label: "Warning" },
              { value: "info", label: "Information" },
            ]}
          />
          <Button disabled={selected.size === 0} onClick={() => setDialogOpen(true)}>
            {strings.qa.resolve} ({selected.size})
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        {loading ? (
          <div className="p-8">
            <Spinner label="Loading flags" />
          </div>
        ) : flags.length === 0 ? (
          <EmptyState title={strings.qa.noFlags} />
        ) : (
          <Table caption={strings.qa.queueTitle}>
            <thead>
              <tr>
                <Th>
                  <span className="sr-only">Select</span>
                </Th>
                <Th>Rule</Th>
                <Th>Severity</Th>
                <Th>What it says</Th>
                <Th>Raised</Th>
                <Th>Isolate</Th>
              </tr>
            </thead>
            <tbody>
              {flags.map((flag) => (
                <tr key={flag.id} className="hover:bg-slate-50">
                  <Td>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300"
                      checked={selected.has(flag.id)}
                      onChange={() => toggle(flag.id)}
                      aria-label={`Select flag ${flag.code}`}
                    />
                  </Td>
                  <Td className="font-mono text-xs">{flag.code}</Td>
                  <Td>
                    <Badge
                      tone={
                        flag.severity === "blocking"
                          ? "blocking"
                          : flag.severity === "warning"
                            ? "warning"
                            : "info"
                      }
                    >
                      {flag.severity}
                    </Badge>
                  </Td>
                  <Td>
                    {flag.message}
                    {RULE_EXPLANATIONS[flag.code] && (
                      <p className="mt-1 text-xs text-slate-500">
                        {RULE_EXPLANATIONS[flag.code]}
                      </p>
                    )}
                  </Td>
                  <Td className="whitespace-nowrap text-xs text-slate-500">
                    {formatDateTime(flag.detected_at)}
                  </Td>
                  <Td>
                    <Link
                      to={`/isolates/${flag.isolate_id}`}
                      className="text-sm font-medium text-brand-700 underline"
                    >
                      Open
                    </Link>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      <Dialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={`${strings.qa.resolve} ${selected.size} flag${selected.size === 1 ? "" : "s"}`}
        description={strings.qa.resolveReasonHint}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>
              {strings.common.cancel}
            </Button>
            <Button
              loading={resolving}
              disabled={reason.trim().length < 3}
              onClick={() => void resolve()}
            >
              {strings.qa.resolve}
            </Button>
          </>
        }
      >
        <Field id="resolve-reason" label={strings.qa.resolveReason} required>
          <Textarea
            id="resolve-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Checked against the register; correct as entered."
          />
        </Field>
      </Dialog>
    </div>
  );
}
